import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Check } from 'lucide-react';
import { CAT_ORDER, money } from './utils';

export default function LibraryPicker({ open, onOpenChange, library, onAdd }) {
  const [selected, setSelected] = useState({});
  useEffect(() => { if (open) setSelected({}); }, [open]);
  const grouped = CAT_ORDER.map(cat => ({ cat, items: library.filter(l => l.category === cat) })).filter(g => g.items.length);
  const count = Object.values(selected).filter(Boolean).length;
  const confirm = () => {
    const chosen = library.filter(l => selected[l.id]);
    onAdd(chosen);
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-2xl" data-testid="library-picker">
        <DialogHeader>
          <DialogTitle style={{ color: '#010A21' }}>Add from Library</DialogTitle>
          <DialogDescription>Select standard services to add to this proposal.</DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[55vh] pr-3">
          <div className="space-y-4">
            {grouped.map(({ cat, items }) => (
              <div key={cat}>
                <p className="text-xs font-semibold mb-1.5" style={{ color: '#031333' }}>{cat}</p>
                <div className="space-y-1.5">
                  {items.map(it => {
                    const on = !!selected[it.id];
                    return (
                      <button
                        key={it.id}
                        type="button"
                        onClick={() => setSelected(s => ({ ...s, [it.id]: !s[it.id] }))}
                        className={`w-full text-left rounded-md border px-3 py-2 transition-colors ${on ? 'border-[#1B4A76] bg-[#EFF6FF]' : 'border-border hover:bg-secondary/30'}`}
                        data-testid={`library-item-${it.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`h-4 w-4 rounded-sm border flex items-center justify-center shrink-0 ${on ? 'bg-[#1B4A76] border-[#1B4A76]' : 'border-border'}`}>{on && <Check className="h-3 w-3 text-white" />}</span>
                              <span className="text-sm font-medium" style={{ color: '#010A21' }}>{it.name}</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5 ml-6">{it.description}</p>
                          </div>
                          <span className="text-sm shrink-0" style={{ fontFamily: "'DM Mono', monospace", color: '#010A21' }}>{money(it.default_fee)}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {grouped.length === 0 && <p className="text-sm text-muted-foreground py-4">No library services available.</p>}
          </div>
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" className="border-border" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-primary text-primary-foreground" disabled={count === 0} onClick={confirm} data-testid="library-add-selected">Add {count > 0 ? `${count} ` : ''}item{count === 1 ? '' : 's'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
